

import { useState, useEffect, useMemo } from "react";
import { X, Calendar, AlertCircle, Mail, Phone, MapPin, FileText, Edit2 } from "lucide-react";
import Badge from "../components/Badge";
import Modal from "../components/Modal";
import VendorForm from "./VendorForm";
import { vendorsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { formatCurrencyFull, formatDate } from "@/lib/data";

interface Vendor {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  type?: string;
  vendor_type?: { id: number; name: string };
  commission_type?: string;
  commission_percent?: number;
  commission_value?: number;
  status?: string;
  balance?: number;
  gst_number?: string;
  contact_person?: string;
}

interface VendorAssignment {
  id: number;
  event_id: number;
  description: string;
  total_amount: number;
  paid_amount: number;
  balance: number;
  status?: string;
  event?: {
    name: string;
    start_date: string;
    end_date?: string;
    status?: string;
  } | null;
}

interface VendorPayment {
  id: number;
  amount: number;
  payment_date: string;
  paid_at?: string;
  payment_method?: string;
  transaction_id?: string;
  reference?: string;
  notes?: string;
  event?: {
    id: number;
    name: string;
  } | null;
}

interface VendorLedger {
  assignments: VendorAssignment[];
  payments: VendorPayment[];
  totalBilled: number;
  totalPaid: number;
  totalBalance: number;
}

interface VendorDetailModalProps {
  isOpen: boolean;
  vendorId: number | null;
  onClose: () => void;
  onVendorUpdated?: () => void;
}

export default function VendorDetailModal({
  isOpen,
  vendorId,
  onClose,
  onVendorUpdated
}: VendorDetailModalProps) {
  const { showToast } = useToast();

  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [ledger, setLedger] = useState<VendorLedger | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isOpen || !vendorId) {
      setVendor(null);
      setLedger(null);
      setError(null);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [vendorRes, ledgerRes] = await Promise.all([
          vendorsApi.get(vendorId),
          vendorsApi.getLedger(vendorId)
        ]);

        const vendorData = vendorRes.data?.data || vendorRes.data;

        if (!vendorData || !vendorData.id) {
          setError("Vendor not found");
          setVendor(null);
          return;
        }

        setVendor(vendorData);

        if (ledgerRes.success && ledgerRes.data) {
          setLedger(ledgerRes.data);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load vendor details";
        setError(message);
        showToast(message, "error");
        setVendor(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, vendorId, showToast]);

  const { upcomingPending, pastPending, completedAssignments, stats } = useMemo(() => {
    if (!ledger?.assignments) {
      return {
        upcomingPending: [],
        pastPending: [],
        completedAssignments: [],
        stats: {
          totalUpcomingPending: 0,
          totalPastPending: 0,
          totalPending: 0,
          upcomingCount: 0,
          pastCount: 0,
          completedCount: 0
        }
      };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcomingPending = ledger.assignments.filter(a => {
      const eventDate = a.event?.start_date ? new Date(a.event.start_date) : null;
      return eventDate && eventDate >= today && a.balance > 0;
    });

    const pastPending = ledger.assignments.filter(a => {
      const eventDate = a.event?.start_date ? new Date(a.event.start_date) : null;
      return eventDate && eventDate < today && a.balance > 0;
    });

    const completedAssignments = ledger.assignments.filter(a => a.balance <= 0);

    const totalUpcomingPending = upcomingPending.reduce((sum, a) => sum + a.balance, 0);
    const totalPastPending = pastPending.reduce((sum, a) => sum + a.balance, 0);

    return {
      upcomingPending,
      pastPending,
      completedAssignments,
      stats: {
        totalUpcomingPending,
        totalPastPending,
        totalPending: totalUpcomingPending + totalPastPending,
        upcomingCount: upcomingPending.length,
        pastCount: pastPending.length,
        completedCount: completedAssignments.length
      }
    };
  }, [ledger]);

  const getStatusBadge = (balance: number, eventDate?: string) => {
    if (balance <= 0) return { label: 'Paid', status: 'success' as const };

    if (eventDate) {
      const date = new Date(eventDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) {
        return { label: 'Overdue', status: 'error' as const };
      }
    }
    return { label: 'Pending', status: 'warning' as const };
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={vendor?.name || "Vendor Details"}
      size="lg"
    >
      {loading ? (
        <div className="space-y-4 py-4">
          <div className="h-6 bg-stone-200 rounded w-32 animate-pulse" />
          <div className="h-4 bg-stone-200 rounded w-48 animate-pulse" />
          <div className="h-32 bg-stone-200 rounded animate-pulse" />
        </div>
      ) : error || !vendor ? (
        <div className="text-center py-8">
          <p className="text-red-600 font-medium">{error || "Vendor not found"}</p>
        </div>
      ) : isEditing ? (
        <div className="py-4">
          <VendorForm
            initialData={{
              name: vendor.name,
              vendor_type_id: vendor.vendor_type?.id?.toString() || "",
              contact_person: vendor.contact_person || "",
              phone: vendor.phone || "",
              email: vendor.email || "",
              address: vendor.address || "",
              gst_number: vendor.gst_number || "",
              commission_type: vendor.commission_type || "fixed",
              commission_value: vendor.commission_value?.toString() || "0",
              status: vendor.status || "active",
            }}
            onSubmit={async (data) => {
              try {
                await vendorsApi.update(vendor.id, data);
                showToast("Vendor updated successfully", "success");
                setIsEditing(false);
                const response = await vendorsApi.get(vendor.id);
                setVendor(response.data?.data || response.data);
                onVendorUpdated?.();
              } catch (error: any) {
                showToast(error?.message || "Failed to update vendor", "error");
              }
            }}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : (
        <div className="space-y-6 py-4">
          {/* Vendor Info Header */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="inline-block px-3 py-1.5 rounded-full text-sm font-medium bg-amber-100 text-amber-700 mb-3">
                {vendor.vendor_type?.name || vendor.type || "Vendor"}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {vendor.email && (
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Mail className="w-4 h-4 text-stone-400" />
                    <span>{vendor.email}</span>
                  </div>
                )}
                {vendor.phone && (
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <Phone className="w-4 h-4 text-stone-400" />
                    <span>{vendor.phone}</span>
                  </div>
                )}
                {vendor.contact_person && (
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <FileText className="w-4 h-4 text-stone-400" />
                    <span>{vendor.contact_person}</span>
                  </div>
                )}
                {vendor.gst_number && (
                  <div className="flex items-center gap-2 text-sm text-stone-600">
                    <FileText className="w-4 h-4 text-stone-400" />
                    <span>GST: {vendor.gst_number}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-1.5 rounded-lg border border-stone-200 text-stone-700 hover:bg-stone-100 transition-colors text-xs font-medium flex items-center gap-1"
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          </div>

          {/* Address */}
          {vendor.address && (
            <div className="bg-stone-50 rounded-lg border border-stone-200 p-4">
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-stone-400" />
                <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Address</p>
              </div>
              <p className="text-sm text-stone-700">{vendor.address}</p>
            </div>
          )}

          {/* Financial Summary */}
          {ledger && (
            <div className="bg-stone-50 rounded-lg border border-stone-200 p-4">
              <h4 className="font-semibold text-stone-800 mb-3">Financial Summary</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white p-3 rounded-lg border border-stone-200 text-center">
                  <p className="text-xs text-stone-500 mb-1">Total Billed</p>
                  <p className="font-bold text-stone-800">{formatCurrencyFull(ledger.totalBilled)}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-stone-200 text-center">
                  <p className="text-xs text-stone-500 mb-1">Total Paid</p>
                  <p className="font-bold text-emerald-600">{formatCurrencyFull(ledger.totalPaid)}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-stone-200 text-center">
                  <p className="text-xs text-stone-500 mb-1">Balance</p>
                  <p className="font-bold text-amber-700">{formatCurrencyFull(ledger.totalBalance)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Upcoming Pending */}
          {upcomingPending.length > 0 && (
            <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
              <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4" />
                Upcoming Pending ({upcomingPending.length})
              </h4>
              <p className="text-sm text-amber-600 mb-3">
                Total: {formatCurrencyFull(stats.totalUpcomingPending)}
              </p>
              <div className="space-y-2">
                {upcomingPending.slice(0, 3).map((assignment: VendorAssignment) => (
                  <div key={assignment.id} className="text-sm bg-white p-3 rounded-lg border border-amber-100">
                    <p className="font-medium text-stone-800">{assignment.event?.name || `Event #${assignment.event_id}`}</p>
                    <p className="text-xs text-stone-500">{formatDate(assignment.event?.start_date || '')}</p>
                    <p className="text-xs font-medium text-amber-600 mt-1">
                      Pending: {formatCurrencyFull(Math.max(0, assignment.balance))}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Overdue Pending */}
          {pastPending.length > 0 && (
            <div className="bg-rose-50 rounded-lg border border-rose-200 p-4">
              <h4 className="font-semibold text-rose-800 flex items-center gap-2 mb-3">
                <AlertCircle className="w-4 h-4" />
                Overdue ({pastPending.length})
              </h4>
              <p className="text-sm text-rose-600 mb-3">
                Total: {formatCurrencyFull(stats.totalPastPending)}
              </p>
              <div className="space-y-2">
                {pastPending.slice(0, 3).map((assignment: VendorAssignment) => (
                  <div key={assignment.id} className="text-sm bg-white p-3 rounded-lg border border-rose-100">
                    <p className="font-medium text-stone-800">{assignment.event?.name || `Event #${assignment.event_id}`}</p>
                    <p className="text-xs text-stone-500">{formatDate(assignment.event?.start_date || '')}</p>
                    <p className="text-xs font-medium text-rose-600 mt-1">
                      Overdue: {formatCurrencyFull(Math.max(0, assignment.balance))}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Pending Message */}
          {upcomingPending.length === 0 && pastPending.length === 0 && ledger?.assignments && ledger.assignments.length > 0 && (
            <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
              <p className="text-sm font-medium text-emerald-700">All payments up to date!</p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
